import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import RefreshIcon from "@mui/icons-material/Refresh"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DuplaConfirmacao from "src/v2/components/DuplaConfirmacao/DuplaConfirmacao"
import theme from "src/v2/theme/theme"
import { normalizeHref } from "src/v2/utils/normalize-href"
import { COMPANY_HISTORY_CONFIG, formatHistoryValue } from "./company-history-config"
import CompanyHistoryForm from "./company-history-form"
import useCompanyHistory from "./use-company-history"

const HistoryState = ({ status, message, onRetry }) => {
    if (status === "loading") return <Typography color="common.white" role="status">Carregando histórico...</Typography>
    if (status === "error") return <Box sx={{ display: "grid", justifyItems: "center", gap: 2 }}><Typography color="common.white" role="alert">{message}</Typography><ButtonWithIcon icon={<RefreshIcon />} onClick={onRetry}>TENTAR NOVAMENTE</ButtonWithIcon></Box>
    return null
}

HistoryState.propTypes = { status: PropTypes.string.isRequired, message: PropTypes.string, onRetry: PropTypes.func.isRequired }

const companyContextFields = [
    { key: "RazaoSocial", label: "Razão social:" },
    { key: "CPFCNPJ", label: "CNPJ:" },
    { key: "Setor", label: "Setor:" },
    { key: "SubSetor", label: "Subsetor:" },
]

const eventContextFields = [
    { key: "RazaoSocial", label: "Razão social:" },
    { key: "CPFCNPJ", label: "CNPJ:" },
    { key: "GrupoEconomico_Nome", label: "Grupo econômico:" },
]

const historyContextFields = {
    rating: companyContextFields,
    probabilityDefault: companyContextFields,
    fact: companyContextFields,
    event: eventContextFields,
}

const getCompanyContextValue = (company, key) => company?.[key] ?? "-"

const CompanyContext = ({ company, fields }) => (
    <Box
        component="section"
        aria-label="Identificação do devedor"
        sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: { xxxs: 2, sm: 3.5 },
            rowGap: 1.5,
            minWidth: 0,
        }}
    >
        {fields.map(({ key, label }, index) => (
            <React.Fragment key={key}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: (currentTheme) => currentTheme.palette.primary.white70, whiteSpace: "nowrap" }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        variant="textBody"
                        color="common.white"
                        sx={{ fontWeight: 600, overflowWrap: "anywhere", wordBreak: "break-word" }}
                    >
                        {getCompanyContextValue(company, key)}
                    </Typography>
                </Box>
                {index < fields.length - 1 && (
                    <Box
                        aria-hidden="true"
                        sx={{
                            display: { xxxs: "none", sm: "block" },
                            height: 24,
                            width: "1px",
                            backgroundColor: (currentTheme) => currentTheme.palette.input.background.white30,
                        }}
                    />
                )}
            </React.Fragment>
        ))}
    </Box>
)

CompanyContext.propTypes = {
    company: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
}

CompanyContext.defaultProps = {
    company: {},
}

const HistoryDetailsDialog = ({ open, company, contextFields, details, row, onClose }) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, color: theme.palette.common.white } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: { xxxs: 2, sm: 3 }, pt: { xxxs: 2, sm: 3 } }}>
            <Typography variant="h3" color="common.white">{details.title}</Typography>
            <IconButton aria-label={`Fechar ${details.title}`} onClick={onClose} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xxxs: 2, sm: 3 }, pb: { xxxs: 2, sm: 3 } }}>
            <Box sx={{ mb: 3 }}><CompanyContext company={company} fields={contextFields} /></Box>
            <Box component="dl" sx={{ display: "grid", gridTemplateColumns: { xxxs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: { xxxs: 2, sm: 3 }, m: 0 }}>
                {details.fields.map((field) => (
                    <Box component="div" key={field.field} sx={{ minWidth: 0 }}>
                        <Typography component="dt" variant="body2" sx={{ color: (currentTheme) => currentTheme.palette.primary.white70, mb: 0.5 }}>{field.label}</Typography>
                        <Typography component="dd" variant="textBody" color="common.white" sx={{ m: 0, fontWeight: 600, overflowWrap: "anywhere" }}>{formatHistoryValue(row[field.field], field.type)}</Typography>
                    </Box>
                ))}
            </Box>
        </DialogContent>
    </Dialog>
)

HistoryDetailsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    company: PropTypes.object,
    contextFields: CompanyContext.propTypes.fields,
    details: PropTypes.shape({
        title: PropTypes.string.isRequired,
        fields: PropTypes.arrayOf(PropTypes.shape({
            field: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.string,
        })).isRequired,
    }).isRequired,
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
}

HistoryDetailsDialog.defaultProps = {
    company: {},
}

const CompanyHistoryModal = ({ open, historyType, company, isAdminUser, onClose, onChanged }) => {
    const definition = COMPANY_HISTORY_CONFIG[historyType]
    const history = useCompanyHistory({ definition, company, onChanged })
    const [editor, setEditor] = useState(null)
    const [rowToDelete, setRowToDelete] = useState(null)
    const [detailsRow, setDetailsRow] = useState(null)
    const canEdit = Boolean(isAdminUser && definition?.editable)
    const contextFields = historyContextFields[historyType]

    const columns = useMemo(() => {
        if (!definition) return []
        const baseColumns = definition.columns
            .filter((column) => !column.adminOnly || isAdminUser)
            .map((column) => ({
                ...column,
                minWidth: column.minWidth ?? 150,
                flex: column.minWidth ? 1.5 : 1,
                valueFormatter: column.type === "link" ? undefined : ({ value }) => formatHistoryValue(value, column.type),
                renderCell: column.type === "link" ? ({ value }) => {
                    const normalizedHref = normalizeHref(value)

                    return normalizedHref ? <IconButton aria-label="Abrir documento do fato relevante" component="a" href={normalizedHref} target="_blank" rel="noopener noreferrer" sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><OpenInNewIcon fontSize="small" /></IconButton> : "-"
                } : undefined,
            }))

        const detailsColumn = definition.details ? [{
            field: "detalhes",
            headerName: "Ações",
            width: 88,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => <Tooltip title="Visualizar detalhes"><IconButton aria-label="Visualizar detalhes da probabilidade de default" onClick={() => setDetailsRow(row)} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>,
        }] : []

        if (!canEdit) return [...baseColumns, ...detailsColumn]
        return [...baseColumns, ...detailsColumn, {
            field: "acoes",
            headerName: "Ações",
            width: 116,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Editar registro"><IconButton aria-label="Editar registro" onClick={() => setEditor({ isNew: false, row })} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Excluir registro"><IconButton aria-label="Excluir registro" onClick={() => setRowToDelete(row)} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
            </Box>,
        }]
    }, [canEdit, definition, isAdminUser])

    if (!definition) return null

    const handleClose = () => {
        setEditor(null)
        setRowToDelete(null)
        setDetailsRow(null)
        onClose()
    }
    const handleAdd = () => setEditor({ isNew: true, row: definition.editable.createDefaults(company) })
    const handleSave = (row) => history.mutate({ method: editor.isNew ? "post" : "patch", row })
    const handleDelete = async () => {
        const deleted = await history.mutate({ method: "delete", row: rowToDelete })
        if (deleted) setRowToDelete(null)
    }

    return <>
        <Dialog open={open} onClose={history.loading ? undefined : handleClose} fullWidth maxWidth={false} PaperProps={{ sx: { m: { xxxs: 0, sm: 2, md: 4 }, width: { xxxs: "100vw", sm: "calc(100vw - 32px)", md: "min(1480px, calc(100vw - 64px))" }, maxWidth: "none", maxHeight: { xxxs: "100dvh", sm: "calc(100dvh - 32px)" }, backgroundColor: theme.palette.background.paper, color: theme.palette.common.white } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: { xxxs: 2, sm: 3 }, pt: { xxxs: 2, sm: 3 } }}>
                <Typography variant="h3" color="common.white">{definition.title}</Typography>
                <IconButton aria-label={`Fechar ${definition.title}`} onClick={handleClose} disabled={history.loading} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: { xxxs: 2, sm: 3 }, pb: { xxxs: 2, sm: 3 }, overflow: "hidden" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xxxs: "flex-start", md: "center" }, flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                    {contextFields ? (
                        <CompanyContext company={company} fields={contextFields} />
                    ) : (
                        <Typography variant="textBody" color="common.white">{company?.RazaoSocial ?? "Empresa selecionada"}</Typography>
                    )}
                    {canEdit && <ButtonWithIcon icon={<AddIcon />} onClick={handleAdd} disabled={history.loading}>{historyType === "rating" ? "ADICIONAR RATING" : "ADICIONAR NOVO"}</ButtonWithIcon>}
                </Box>
                {history.status === "error" ? <Box sx={{ minHeight: 260, display: "grid", placeItems: "center" }}><HistoryState status={history.status} message={history.error} onRetry={history.load} /></Box> : history.status === "empty" ? (
                    <Box sx={{ minHeight: 260, display: "grid", placeItems: "center", textAlign: "center", px: 2 }}>
                        <Typography color="common.white">{definition.emptyText}</Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: "clamp(320px, 58dvh, 620px)", minHeight: 0 }}>
                        <DataGridTable contained showPagination rows={history.rows} columns={columns} loading={history.loading} getRowId={(row) => row.id ?? row.ID} />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
        {detailsRow && <HistoryDetailsDialog open company={company} contextFields={contextFields} details={definition.details} row={detailsRow} onClose={() => setDetailsRow(null)} />}
        {editor && <CompanyHistoryForm open title={editor.isNew ? `Adicionar — ${definition.title}` : `Editar — ${definition.title}`} fields={definition.editable.fields} initialRow={editor.row} saving={history.loading} onClose={() => setEditor(null)} onSave={handleSave} />}
        <DuplaConfirmacao open={Boolean(rowToDelete)} title="Excluir registro" description="Esta ação não pode ser desfeita. Deseja excluir este registro?" cancel={() => setRowToDelete(null)} confirm={handleDelete} confirmTextButton={history.loading ? "EXCLUINDO..." : "EXCLUIR"} />
    </>
}

CompanyHistoryModal.propTypes = { open: PropTypes.bool.isRequired, historyType: PropTypes.oneOf(Object.keys(COMPANY_HISTORY_CONFIG)).isRequired, company: PropTypes.object, isAdminUser: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onChanged: PropTypes.func }
CompanyHistoryModal.defaultProps = { company: {}, onChanged: undefined }

export default CompanyHistoryModal
